import { Injectable, signal } from '@angular/core';

import { Breadcrumb } from './breadcrumb.interface';

/**
 * How a page names itself in the trail once it knows its own record.
 *
 * Route data cannot carry "LFPB — Le Bourget" or an account holder's name:
 * those are known only after the record loads. A page sets `lastLabel` when it
 * has one, or replaces the whole `trail` when even its ancestry is dynamic.
 *
 * Ported from evo-accounts, minus its second-from-last positional overrides.
 * Nothing in v2 nests deeply enough to need them, and an unused override is a
 * setter someone will eventually reach for without knowing what reads it.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbOverrideService {
  /**
   * A complete trail, replacing the one the route declares. Set this when a
   * page's ancestry is as dynamic as its leaf; the positional override below
   * is then ignored.
   */
  readonly trail = signal<Breadcrumb[] | null>(null);

  /** The leaf's label, for the common case of a page named after its record. */
  readonly lastLabel = signal<string | null>(null);

  /** The leaf's link target, where the leaf should stay clickable. */
  readonly lastRoute = signal<string | null>(null);

  reset(): void {
    this.trail.set(null);
    this.lastLabel.set(null);
    this.lastRoute.set(null);
  }
}
